import React, { useEffect, useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth, isStudent } from "@/lib/auth";
import { fmtDateTime } from "@/lib/format";

/**
 * Reusable thread messages panel. `endpoint` = "requests" | "threads".
 * Messages are polled every 20s.
 */
export default function ThreadMessages({ endpoint, resourceId, messages, refetch, allowInput = true }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const sendMut = useMutation({
    mutationFn: async () => (await api.post(`/${endpoint}/${resourceId}/messages`, { body })).data,
    onSuccess: () => {
      setBody("");
      refetch?.();
      qc.invalidateQueries({ predicate: (q) => Array.isArray(q.queryKey) && (q.queryKey[0] === "request" || q.queryKey[0] === "thread") });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore invio"),
  });

  return (
    <div className="flex flex-col h-full">
      <div ref={listRef} className="flex-1 overflow-y-auto space-y-3 p-4 bg-stone-50 rounded-lg border border-stone-200 min-h-[320px] max-h-[520px]">
        {(!messages || messages.length === 0) ? (
          <div className="h-full grid place-items-center text-stone-400 text-sm py-16">Nessun messaggio ancora.</div>
        ) : messages.map((m, i) => {
          const mine = m.sender_id === user?.id;
          const isSystem = m.is_system;
          return (
            <div
              key={m.id || i}
              data-testid={`msg-${m.id}`}
              className={`flex ${mine ? "justify-end" : "justify-start"} animate-in-row`}
              style={{ animationDelay: `${i * 10}ms` }}
            >
              {isSystem ? (
                <div className="mx-auto text-[11px] text-stone-500 bg-white border border-stone-200 rounded-full px-3 py-1">
                  {m.body}
                </div>
              ) : (
                <div className={`max-w-[76%] rounded-2xl px-4 py-2.5 ${
                  mine ? "bg-[#1C352D] text-white rounded-br-sm" : "bg-white border border-stone-200 rounded-bl-sm"
                }`}>
                  <div className={`text-[11px] mb-0.5 flex items-center gap-1 ${mine ? "text-stone-300" : "text-stone-500"}`}>
                    <User size={10}/> {m.sender_name || "—"} · {m.sender_role_label || ""}
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                  <div className={`text-[10px] mt-1 ${mine ? "text-stone-300" : "text-stone-400"}`}>{fmtDateTime(m.created_at)}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {allowInput && (
        <form
          onSubmit={(e) => { e.preventDefault(); if (body.trim()) sendMut.mutate(); }}
          className="mt-3 flex gap-2 items-end"
        >
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={isStudent(user) ? "Scrivi alla segreteria…" : "Rispondi allo studente…"}
            rows={2}
            className="flex-1 bg-white"
            data-testid="thread-message-input"
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                if (body.trim()) sendMut.mutate();
              }
            }}
          />
          <Button
            type="submit"
            data-testid="thread-message-send"
            disabled={!body.trim() || sendMut.isPending}
            className="bg-[#1C352D] hover:bg-[#264A3E] h-10 rounded-full px-5"
          >
            <Send size={14} className="mr-1.5"/> Invia
          </Button>
        </form>
      )}
    </div>
  );
}
